import React, { useEffect, useState } from "react";
import { fmtIST } from "../lib/time";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Plus, CheckCircle2, XCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import { Dialog, Input } from "./Suppliers";
import { useAuth } from "../context/AuthContext";
import { usePager } from "../components/Pagination";
import DeleteRequestButton from "../components/DeleteRequestButton";
import useSubmitGuard from "../lib/useSubmitGuard";
import { fmtQty } from "../lib/format";

export default function MaterialRequests() {
  // Feb-2026 — submit guard prevents double-tap duplicates on tablets.
  const { busy: submitBusy, guard: submitGuard, reset: resetGuard } = useSubmitGuard();
  const [rows, setRows] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [decideOf, setDecideOf] = useState(null);
  // Form items get a synthetic `_rowId` so React tracks per-row state
  // correctly across add/remove (using array index breaks state on remove).
  const _rowId = () => `r-${Math.random().toString(36).slice(2, 9)}`;
  const [items, setItems] = useState([{ _rowId: _rowId(), material_id: "", qty: 1, notes: "" }]);
  const [neededBy, setNeededBy] = useState("");
  const [order, setOrder] = useState("");
  const [remarks, setRemarks] = useState("");
  const { user } = useAuth();

  const [editOf, setEditOf] = useState(null);   // Wave-23 Ch-7: sent-back MR being edited
  // User #7 — live available stock per material; lazy-fetched on row select
  const [availMap, setAvailMap] = useState({});  // { [material_id]: number }
  // Feb-2026 — include archived (soft-deleted) MRs in the list. Default off.
  const [includeArchived, setIncludeArchived] = useState(false);
  // Live-Prod-Fix #2 — list of approved DCs available for pre-fill into the
  // MR form. Loaded alongside materials.
  const [dcs, setDcs] = useState([]);
  const [prefillDc, setPrefillDc] = useState("");

  const fetchAvail = async (mid) => {
    if (!mid || availMap[mid] !== undefined) return;
    try {
      const r = await api.get(`/materials/${mid}/available-stock`);
      setAvailMap((m) => ({ ...m, [mid]: r.data.available || 0 }));
    } catch { /* silent */ }
  };

  const load = async () => {
    const [r, m, c] = await Promise.all([
      api.get(`/material-requests${includeArchived ? "?include_deleted=true" : ""}`),
      api.get("/materials"),
      api.get("/challans"),
    ]);
    setRows(r.data); setMaterials(m.data);
    // Live-Prod-Fix #2 — only show APPROVED (issued / invoice_pending)
    // non-closed DCs in the pre-fill dropdown, newest first.
    setDcs((c.data || []).filter((x) => ["issued", "invoice_pending", "closed"].includes(x.status)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeArchived]);

  const matCode = (id) => materials.find((m) => m.id === id)?.code || "";
  const matName = (id) => materials.find((m) => m.id === id)?.name || "—";

  const updateItem = (i, patch) => {
    setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
    if (patch.material_id) fetchAvail(patch.material_id);
  };
  const addItem = () => setItems([...items, { _rowId: _rowId(), material_id: "", qty: 1, notes: "" }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  // Feb-2026 — direct archive (soft-delete) and restore for the owner / PH / SA.
  // List endpoint already supports `?include_deleted=true` for the toggle.
  const archiveMr = async (r) => {
    if (!window.confirm(`Archive ${r.number}? It will be hidden from the default list.\nPlant Head / Super Admin can restore it.`)) return;
    try {
      await api.delete(`/material-requests/${r.id}`);
      toast.success("Archived");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };
  const restoreMr = async (r) => {
    try {
      await api.post(`/material-requests/${r.id}/restore`);
      toast.success("Restored");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };

  const openEdit = (r) => {
    setEditOf(r);
    setItems((r.items || []).map((it) => ({ _rowId: _rowId(), material_id: it.material_id, qty: it.qty, notes: it.notes || "" })));
    setOrder(r.production_order || "");
    setRemarks(r.remarks || "");
    setNeededBy(r.needed_by || "");
    setOpen(true);
    // Pre-fetch availability for each existing row
    (r.items || []).forEach((it) => fetchAvail(it.material_id));
  };

  const closeDialog = () => {
    setOpen(false); setEditOf(null);
    setItems([{ _rowId: _rowId(), material_id: "", qty: 1, notes: "" }]); setRemarks(""); setOrder(""); setNeededBy("");
    setPrefillDc("");
  };

  // Live-Prod-Fix #2 — pre-fill MR items from the selected Delivery Challan.
  const applyDcPrefill = (dcId) => {
    setPrefillDc(dcId);
    if (!dcId) return;
    const dc = dcs.find((d) => d.id === dcId);
    if (!dc) return;
    const prefilled = (dc.items || [])
      .filter((it) => it.material_id)
      .map((it) => ({
        _rowId: _rowId(),
        material_id: it.material_id,
        qty: Number(it.qty) || 1,
        notes: `From DC ${dc.number}`,
      }));
    if (!prefilled.length) {
      toast.error("Selected DC has no material-linked line items.");
      return;
    }
    setItems(prefilled);
    setRemarks((r) => r ? r : `Pre-filled from Delivery Challan ${dc.number}`);
    // Fetch availability for each row so the Available column lights up.
    prefilled.forEach((p) => fetchAvail(p.material_id));
    toast.success(`${prefilled.length} item(s) copied from ${dc.number}`);
  };

  const submit = async () => {
    if (items.some((i) => !i.material_id)) { toast.error("Material required for each row"); return; }
    // User #7 — client-side check: qty <= available stock & > 0 (allow decimals)
    for (const it of items) {
      const q = Number(it.qty);
      if (!q || q <= 0) { toast.error(`${matCode(it.material_id) || "Row"}: quantity must be > 0`); return; }
      const avail = availMap[it.material_id];
      if (avail !== undefined && q > Number(avail)) {
        toast.error(`${matCode(it.material_id)}: requested ${q} exceeds available ${fmtQty(avail)}`);
        return;
      }
      if (Number(avail) === 0) {
        toast.error(`${matCode(it.material_id)}: no stock available — cannot raise MR`);
        return;
      }
    }
    try {
      await submitGuard(async (idemKey) => {
        if (editOf) {
          await api.post(`/material-requests/${editOf.id}/resubmit`, {
            items: items.map((i) => ({ material_id: i.material_id, qty: Number(i.qty), notes: i.notes })),
            production_order: order, remarks,
          }, { headers: { "Idempotency-Key": idemKey } });
          toast.success(`MR ${editOf.number} resubmitted for approval`);
        } else {
          await api.post("/material-requests", {
            items: items.map((i) => ({ material_id: i.material_id, qty: Number(i.qty), notes: i.notes })),
            needed_by: neededBy || null, production_order: order, remarks,
          }, { headers: { "Idempotency-Key": idemKey } });
          toast.success("Request submitted");
        }
      });
      closeDialog();
      resetGuard();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const decide = async (decision) => {
    if (decision === "send_back" && !(decideOf.remark || "").trim()) {
      toast.error("Reason required for Send Back"); return;
    }
    try {
      if (decision === "send_back") {
        await api.post(`/material-requests/${decideOf.id}/send-back`, { remark: decideOf.remark });
        toast.success("Sent back to creator");
      } else {
        await api.post(`/material-requests/${decideOf.id}/approve`, { decision, remark: decideOf.remark || "" });
        toast.success(decision);
      }
      setDecideOf(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const isApprover = user?.role?.is_super_admin || user?.role?.name === "Plant Head/Admin";
  const isSA = !!user?.role?.is_super_admin;
  // Wave-J pagination
  const { visibleRows: pagedRows, controls: pageControls } = usePager(rows, 10);

  return (
    <div data-testid="mrs-page">
      <PageHeader title="Material Requests" subtitle="Requester → Plant Head approves → Outward enabled"
        helpText="Production raises a daily material request. Once approved by Plant Head/SA, the materials can be issued via Outward Scan against the linked Production / Work Order."
        right={<button onClick={() => setOpen(true)} className="bg-[#1565C0] hover:bg-[#0D2B5E] text-white text-sm px-4 py-2 rounded-sm flex items-center gap-2 uppercase tracking-wider font-semibold" data-testid="new-mr-btn"><Plus size={14}/> New Request</button>}
      />
      <div className="p-8">
        <div className="flex items-center gap-3 mb-3">
          <label className="flex items-center gap-2 text-xs text-[#0D2B5E]/80 cursor-pointer" data-testid="mr-toggle-archived">
            <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} className="accent-[#1565C0]"/>
            <span className="tracking-widest uppercase font-bold">Include archived</span>
          </label>
        </div>
        <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-4 py-2.5">Number</th>
                <th className="text-left px-4 py-2.5">Requester</th>
                <th className="text-right px-4 py-2.5"># Items</th>
                <th className="text-left px-4 py-2.5">Production Order</th>
                <th className="text-left px-4 py-2.5">Needed By</th>
                <th className="text-left px-4 py-2.5">Approver</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((r, i) => (
                <tr key={r.id} className={`${i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"} ${r.deleted_at ? "opacity-50" : ""}`} data-testid={`mr-row-${r.number}`}>
                  <td className="px-4 py-2.5 font-mono text-[#1565C0]">
                    {r.number}
                    {r.deleted_at && <span className="ml-2 inline-flex items-center text-[9px] uppercase tracking-widest font-bold bg-[#0D2B5E]/10 text-[#0D2B5E]/60 px-1.5 py-0.5 rounded-sm" data-testid={`mr-archived-${r.number}`}>Archived</span>}
                    {/* Live-Prod-Fix #2 — mark MRs auto-raised from a DC */}
                    {r.linked_dc_number && (
                      <div className="mt-0.5 text-[9px] uppercase tracking-widest font-bold text-[#2E7D32]" data-testid={`mr-src-dc-${r.number}`}>
                        ⟵ {r.linked_dc_number}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs">{r.created_by_name}</td>
                  <td className="px-4 py-2.5 font-mono text-right">{r.items.length}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.production_order || "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{r.needed_by || "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{r.approved_by || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: r.status === "approved" || r.status === "fulfilled" ? "#2E7D32" : r.status === "rejected" ? "#C62828" : r.status === "partially_fulfilled" ? "#1565C0" : "#E65100" }}>{r.status.replace("_", " ")}</span>
                    {r.overridden_by && <div className="text-[9px] text-[#E65100] tracking-widest uppercase mt-0.5">SA OVERRIDE</div>}
                    {/* Wave-23 Ch-5: balance chips so the list itself answers
                        "what's left to issue" without opening the modal. */}
                    {(r.status === "partially_fulfilled" || r.status === "approved") && r.items?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1 max-w-[260px]" data-testid={`mr-bal-chips-${r.number}`}>
                        {r.items.map((it) => {
                          const done = (r.fulfilled_qty || {})[it.material_id] || 0;
                          const bal = Math.max(0, Number(it.qty || 0) - Number(done));
                          if (bal === 0) return null;
                          return (
                            <span key={it.material_id} className="text-[9px] font-mono bg-[#FFB74D]/15 border border-[#FFB74D]/40 text-[#0D2B5E] px-1.5 py-0.5 rounded-sm" title={it.material_name || ""}>
                              {(it.material_code || it.material_id.slice(-5))}:{fmtQty(bal)}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right space-x-1">
                    <button onClick={() => setView(r)} className="text-[#1565C0]" data-testid={`view-mr-${r.number}`}><Eye size={14}/></button>
                    {r.status === "pending_approval" && isApprover && (
                      <button onClick={() => setDecideOf(r)} className="text-xs bg-[#2E7D32] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold" data-testid={`decide-mr-${r.number}`}>Review</button>
                    )}
                    {/* Wave-23 Ch-7: sent-back MRs are editable & re-submittable by the original creator (or SA). */}
                    {r.status === "sent_back" && (r.created_by === user?.id || isSA) && (
                      <button onClick={() => openEdit(r)} className="text-xs bg-[#1565C0] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold" data-testid={`edit-mr-${r.number}`}>Edit &amp; Resubmit</button>
                    )}
                    {(r.status === "approved" || r.status === "rejected") && isSA && (
                      <button onClick={() => setDecideOf(r)} className="text-xs bg-[#E65100] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold" data-testid={`override-mr-${r.number}`}>Override</button>
                    )}
                    {/* Feb-2026 — direct soft-delete (archive). Hidden once already archived. */}
                    {!r.deleted_at && (r.created_by === user?.id || isApprover) && !["partially_fulfilled", "fully_issued", "fulfilled"].includes(r.status) && (
                      <button onClick={() => archiveMr(r)} className="text-[10px] uppercase tracking-widest font-bold text-[#0D2B5E]/60 hover:text-[#C62828]" data-testid={`archive-mr-${r.number}`} title="Archive (soft delete)">Archive</button>
                    )}
                    {r.deleted_at && isApprover && (
                      <button onClick={() => restoreMr(r)} className="text-[10px] uppercase tracking-widest font-bold text-[#2E7D32]" data-testid={`restore-mr-${r.number}`} title="Restore from archive">Restore</button>
                    )}
                    <span className="inline-block align-middle"><DeleteRequestButton module="material_request" record={r} label={r.number} onDeleted={load}/></span>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-[#0D2B5E]/50">No requests yet.</td></tr>}
            </tbody>
          </table>
          {pageControls}
        </div>
      </div>

      {open && (
        <Dialog title={editOf ? `Edit & Resubmit · ${editOf.number}` : "New Material Request"} onClose={closeDialog} wide>
          {editOf && editOf.send_back_remark && (
            <div className="bg-[#E65100]/5 border border-[#E65100]/30 p-3 mb-4 rounded-sm">
              <div className="text-[10px] tracking-widest uppercase font-bold text-[#E65100]">Sent-back reason</div>
              <div className="text-sm text-[#0D2B5E] mt-0.5">{editOf.send_back_remark}</div>
              <div className="text-[10px] text-[#0D2B5E]/60 mt-1">by {editOf.sent_back_by} · {fmtIST(editOf.sent_back_at)}</div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <Input label="Production / Work Order" value={order} onChange={setOrder} testId="mr-order"/>
            <Input label="Needed By" type="date" value={neededBy} onChange={setNeededBy}/>
            <Input label="Remarks" value={remarks} onChange={setRemarks}/>
          </div>
          {/* Live-Prod-Fix #2 — pre-fill from an approved Delivery Challan.
              Hidden when resubmitting a sent-back MR (editOf) to avoid
              accidentally wiping the resubmission items. */}
          {!editOf && dcs.length > 0 && (
            <div className="bg-[#F5F5F5] border border-[#EEEEEE] rounded-sm p-3 mb-4 flex items-center gap-3">
              <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/70 shrink-0">Pre-fill from DC</div>
              <select
                value={prefillDc}
                onChange={(e) => applyDcPrefill(e.target.value)}
                data-testid="mr-prefill-dc"
                className="flex-1 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm bg-white"
              >
                <option value="">Select a Delivery Challan to copy items from…</option>
                {dcs.map((d) => {
                  const n = (d.items || []).filter((x) => x.material_id).length;
                  const label = `${d.number} — ${d.party_name} (${d.type}, ${n} item${n === 1 ? "" : "s"})`;
                  return <option key={d.id} value={d.id}>{label}</option>;
                })}
              </select>
              {prefillDc && (
                <button
                  type="button"
                  onClick={() => applyDcPrefill("")}
                  className="text-[10px] uppercase tracking-widest text-[#C62828] font-bold"
                >Clear</button>
              )}
            </div>
          )}
          <div className="flex justify-between items-center mb-2">
            <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60">Items</div>
            <button onClick={addItem} className="text-xs text-[#1565C0] font-semibold uppercase tracking-wider" data-testid="mr-add-item">+ Add item</button>
          </div>
          <table className="w-full text-sm border border-[#EEEEEE]">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr><th className="text-left px-2 py-1">Material</th><th className="text-right px-2 py-1">Available</th><th className="text-right px-2 py-1">Qty</th><th className="text-left px-2 py-1">Notes</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const avail = it.material_id ? availMap[it.material_id] : undefined;
                const qNum = Number(it.qty || 0);
                const exceeds = avail !== undefined && qNum > avail;
                const zero = avail === 0;
                return (
                <tr key={it._rowId || `row-${i}`}>
                  <td className="p-1">
                    <select value={it.material_id} onChange={(e) => updateItem(i, { material_id: e.target.value })} data-testid={`mr-mat-${i}`} className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm">
                      <option value="">Select…</option>
                      {materials.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
                    </select>
                  </td>
                  <td className="p-1 text-right text-xs font-mono">
                    {it.material_id ? (
                      avail === undefined ? <span className="text-[#0D2B5E]/40">…</span> :
                      <span className={zero ? "text-[#C62828] font-bold" : (exceeds ? "text-[#E65100] font-bold" : "text-[#2E7D32] font-bold")} data-testid={`mr-avail-${i}`}>
                        {fmtQty(avail)}{zero && " · no stock"}
                      </span>
                    ) : <span className="text-[#0D2B5E]/40">—</span>}
                  </td>
                  <td className="p-1">
                    <input type="number" step="any" min="0" max={avail || undefined} value={it.qty} onChange={(e) => updateItem(i, { qty: e.target.value })} data-testid={`mr-qty-${i}`}
                      className={`w-24 border px-2 py-1 text-xs rounded-sm font-mono text-right ${exceeds || zero ? "border-[#C62828] bg-[#C62828]/5" : "border-[#EEEEEE]"}`}/>
                  </td>
                  <td className="p-1"><input value={it.notes} onChange={(e) => updateItem(i, { notes: e.target.value })} className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm"/></td>
                  <td className="p-1">{items.length > 1 && <button onClick={() => removeItem(i)} className="text-[#C62828] text-xs">×</button>}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={submit} disabled={submitBusy} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase disabled:opacity-50 disabled:cursor-not-allowed" data-testid="save-mr-btn">{submitBusy ? "Submitting…" : (editOf ? "Resubmit for Approval" : "Submit")}</button>
          </div>
        </Dialog>
      )}

      {view && (
        <Dialog title={`Material Request ${view.number}`} onClose={() => setView(null)} wide>
          <table className="w-full text-sm border border-[#EEEEEE]">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr><th className="text-left px-2 py-1">Material</th><th className="text-right px-2 py-1">Qty</th><th className="text-left px-2 py-1">Notes</th></tr>
            </thead>
            <tbody>
              {view.items.map((it, i) => (
                <tr key={`${it.material_id || "row"}-${i}`} className={i % 2 === 0 ? "" : "bg-[#F5F5F5]/40"}>
                  <td className="px-2 py-1">{matCode(it.material_id)} — {matName(it.material_id)}</td>
                  <td className="px-2 py-1 font-mono text-right">{fmtQty(it.qty)}</td>
                  <td className="px-2 py-1 text-xs">{it.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {view.approver_remark && <div className="text-xs mt-2"><strong>{view.approved_by}:</strong> {view.approver_remark}</div>}
          {view.overridden_by && (
            <div className="text-xs mt-2 p-2 bg-[#E65100]/10 border-l-2 border-[#E65100]">
              <strong className="uppercase tracking-widest text-[10px] text-[#E65100]">SA Override</strong><br/>
              {view.overridden_by} reversed the previous "{view.overridden_from}" decision on {fmtIST(view.overridden_at)}
            </div>
          )}
        </Dialog>
      )}

      {decideOf && (
        <Dialog title={`Review ${decideOf.number}`} onClose={() => setDecideOf(null)}>
          {(decideOf.status === "approved" || decideOf.status === "rejected") && (
            <div className="mb-3 text-[11px] text-[#E65100] bg-[#E65100]/10 border-l-2 border-[#E65100] px-3 py-2 uppercase tracking-widest font-bold">
              SA Override · current status: {decideOf.status.replace("_", " ")}
            </div>
          )}
          <textarea placeholder="Remark (optional)" value={decideOf.remark || ""} onChange={(e) => setDecideOf({ ...decideOf, remark: e.target.value })} rows={3} data-testid="mr-remark" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm"/>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => decide("reject")} className="px-4 py-2 bg-[#C62828] text-white text-sm rounded-sm font-semibold tracking-wider uppercase flex items-center gap-2" data-testid="mr-reject-btn"><XCircle size={14}/>Reject</button>
            <button onClick={() => decide("send_back")} className="px-4 py-2 bg-[#E65100] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="mr-sendback-btn">Send Back</button>
            <button onClick={() => decide("approve")} className="px-4 py-2 bg-[#2E7D32] text-white text-sm rounded-sm font-semibold tracking-wider uppercase flex items-center gap-2" data-testid="mr-approve-btn"><CheckCircle2 size={14}/>Approve</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
